import Blink from '@components/Blink'
import useCurrentSection from '@hooks/useCurrentSection'
import { MainMenuItem } from '@hooks/useNavigation'
import usePageMetadata from '@hooks/usePageMetadata'
import { FunctionComponent, useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import SecondLevelMenu from './SecondLevelMenu'
import useTrackingUtils from '../../useTrackingUtils'
import AnimatedPlusMinusButton from '@components/Buttons/AnimatedPlusMinusButton'

const ItemWrapper = styled.div`
  ${({
    theme: {
      fonts: { goodOT },
      colors: {
        grey: { grey_10 },
      },
    },
  }) => css`
    height: 40px;
    font-family: ${goodOT};
    font-weight: 800;
    font-size: 17px;
    line-height: 20px;
    padding: 0 24px;
    display: flex;
    flex-direction: row;
    align-items: center;
    border-bottom: 1px solid ${grey_10};
  `}
`

interface StyledBlinkProps {
  isActive: boolean
  sectionColor: string
}

const StyledBlink = styled(Blink)<StyledBlinkProps>`
  ${({
    theme: {
      colors: {
        grey: { grey_100 },
      },
    },
    isActive,
    sectionColor,
  }) => css`
    color: ${isActive ? sectionColor : grey_100};
    text-decoration: none;
    flex: 1 auto;
  `}
`

const StyledItem = styled.button<StyledBlinkProps>`
  ${({
    theme: {
      fonts: { goodOT },
      colors: {
        grey: { grey_100 },
      },
    },
    isActive,
    sectionColor,
  }) => css`
    color: ${isActive ? sectionColor : grey_100};
    background-color: transparent;
    border: 0;
    cursor: pointer;
    padding: 0;
    line-height: 0;
    font-family: ${goodOT};
    font-weight: 800;
    font-size: 17px;
    line-height: 20px;
  `}
`

interface SectionIndicatorProps {
  sectionColor: string
}

const SectionIndicator = styled.div<SectionIndicatorProps>`
  ${({ sectionColor }) => css`
    width: 4px;
    height: 14px;
    background-color: ${sectionColor};
    margin-right: 12px;
  `}
`

const ItemAndSubmenuWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const FirstLevelItem: FunctionComponent<MainMenuItem> = ({
  title,
  primaryColor,
  link,
  content,
}) => {
  const { isActive } = useCurrentSection(link.title || '')
  const [isSubmenuVisible, setIsSubMenuVisible] = useState(isActive)
  const [mountChildren, setMountChildren] = useState(false)
  const pageMetadata = usePageMetadata()

  const { burgerMenuClickTrackingFn } = useTrackingUtils({
    title,
    url: link.href,
  })

  useEffect(() => {
    if (isSubmenuVisible) {
      setMountChildren(true)
    }
  }, [isSubmenuVisible])

  useEffect(() => {
    setIsSubMenuVisible(isActive)
  }, [isActive, pageMetadata])

  const toggleSubmenu = () => setIsSubMenuVisible((prevValue) => !prevValue)

  return (
    <ItemAndSubmenuWrapper>
      <ItemWrapper>
        <SectionIndicator sectionColor={primaryColor} />
        {!link.href ? (
          <StyledItem
            onClick={toggleSubmenu}
            aria-label={title}
            isActive={isActive}
            sectionColor={primaryColor}>
            {title}
          </StyledItem>
        ) : (
          <StyledBlink
            {...link}
            isActive={isActive}
            sectionColor={primaryColor}
            onClick={burgerMenuClickTrackingFn}>
            {title}
          </StyledBlink>
        )}
        {content?.length && (
          <AnimatedPlusMinusButton
            onClick={toggleSubmenu}
            isPlus={!isSubmenuVisible}
          />
        )}
      </ItemWrapper>
      {mountChildren && (
        <SecondLevelMenu isVisible={isSubmenuVisible}>
          {content}
        </SecondLevelMenu>
      )}
    </ItemAndSubmenuWrapper>
  )
}

export default FirstLevelItem
